// @angular
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  AfterViewInit,
  EventEmitter,
  Output,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { Subscription } from "rxjs";
// Own services
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { NavbarMenuService } from "./navbar-menu.service";
import { ThemeService } from "../../../themes/theme.service";
// Variables
import { NAVBAR_SHORTCUTS } from "./navbar-shortcuts";
import { CUSTOM_URLS } from "../../../screens/login/CUSTOM_URLS";
import { PROFILES } from "../../../../assets/profiles/profiles";
// Interfaces
import { Theme } from "../../../themes/symbols";
import { MenuItem } from "./navbar-menu";
import { TranslateModule } from "@ngx-translate/core";
import { GasEffectComponent } from "../nav-effects/gas-effect/gas-effect.component";
import { WaterEffectComponent } from "../nav-effects/water-effect/water-effect.component";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { NgClass, NgStyle } from "@angular/common";

/**
 * Collapsible side navigation built from the session `navbarMenu`, with up to three submenu levels, custom logo and gas/water effects.
 * Alt+Shift+key shortcuts navigate to routes (`NAVBAR_SHORTCUTS`); emits `sidebarUpdate`.
 */
@Component({
  selector: "app-navbar-side",
  templateUrl: "./navbar-side.component.html",
  styleUrls: ["./navbar-side.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    NgClass,
    MatMenuModule,
    MatIconModule,
    WaterEffectComponent,
    GasEffectComponent,
    NgStyle,
    TranslateModule
  ]
})
export class NavbarSideComponent implements OnInit, OnDestroy, AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  sessionProfile: string;
  readonly PROFILES = PROFILES;

  // Logo
  @Input() logoImgUrl: any;
  logoLoading: boolean = true;
  customLogo = CUSTOM_URLS.find(url => window.location.href.includes(url.urlText));
  sidebarLogo: string = this.customLogo
    ? this.customLogo.sidebarImg
    : "assets/img/logos/arsondata_white.png";

  // Menus
  menuList: MenuItem[];
  activeTheme: Theme;
  sidebarToggled: boolean = true;
  sidebarLocked: boolean = false;
  @Output() sidebarUpdate = new EventEmitter<boolean>();

  // Theme change subscription
  themeUpdatedSub: Subscription;

  // Submenu
  @ViewChild("sidebar") sidebar: ElementRef;
  activeSubmenu: MenuItem[];
  activeSubmenuSubmenu: MenuItem[];
  activeSubmenuSubmenuSubmenu: MenuItem[];
  menuActive: boolean = false;
  submenuActive: boolean = false;
  submenuSubmenuActive: boolean = false;
  menuOpenDelay: number = 500;
  menuCollapseDelay: number = 500;
  menuClosing: ReturnType<typeof setTimeout>;
  submenuClosing: ReturnType<typeof setTimeout>;
  submenuSubmenuClosing: ReturnType<typeof setTimeout>;
  sidebarCollapseTimeout: ReturnType<typeof setTimeout>;
  sidebarOpenTimeout: ReturnType<typeof setTimeout>;

  // Keyboard shortcuts
  @HostListener("document:keydown", ["$event"])
  handleKeyboardDownEvent(event: KeyboardEvent) {
    if (event.shiftKey && event.altKey) {
      event.preventDefault();
      event.stopPropagation();
      if (NAVBAR_SHORTCUTS[event.key.toUpperCase()]) {
        this.router.navigate([NAVBAR_SHORTCUTS[event.key.toUpperCase()]]);
      }
    }
  }

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    readonly navbarMenu: NavbarMenuService,
    readonly router: Router,
    readonly SessionDataService: SessionDataService,
    readonly theme: ThemeService
  ) {
    effect(() => {
      const profile = this.SessionDataService.profile();
      if (profile) {
        this.sessionProfile = profile;
      }
    });
    effect(() => {
      const navbarMenu = this.SessionDataService.navbarMenu();
      if (navbarMenu) {
        this.menuList = navbarMenu;
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.sessionProfile = untracked(() => this.SessionDataService.profile());
    this.navbarMenu.loadMenu();
    this.menuList = untracked(() => this.SessionDataService.navbarMenu());
    this.activeTheme = this.theme.getActiveTheme();

    // Theme updates
    this.themeUpdatedSub = this.theme.themeChange.subscribe(
      (activeTheme: Theme) => (this.activeTheme = activeTheme)
    );
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {
    this.themeUpdatedSub.unsubscribe();
    clearTimeout(this.sidebarOpenTimeout);
    clearTimeout(this.submenuClosing);
    clearTimeout(this.menuClosing);
    clearTimeout(this.sidebarCollapseTimeout);
  }

  /***************************************************************************/
  // ANCHOR After view init
  /***************************************************************************/

  ngAfterViewInit(): void {
    this.sidebar.nativeElement.classList.remove();
  }

  /***************************************************************************/
  // ANCHOR Methods
  /***************************************************************************/

  // Logo load
  onLogoLoad(): void {
    this.logoLoading = false;
  }

  // Submenu
  openSubmenu(target: HTMLElement, menu: MenuItem): void {
    // Vertical overflow control
    let submenuTop = this.checkTop(target, menu);
    // Vertical position
    this.sidebar.nativeElement.style.setProperty("--sidebar-submenu-top", submenuTop + "px");
    this.activeSubmenu = menu.submenu;
    this.activeSubmenuSubmenu = null;
    this.activeSubmenuSubmenuSubmenu = null;
  }

  // Submenu of submenu
  openSubmenuSubmenu(target: HTMLElement, submenu: MenuItem): void {
    // Vertical overflow control
    let submenuTop = this.checkTop(target, submenu, true);
    // Vertical position
    this.sidebar.nativeElement.style.setProperty(
      "--sidebar-submenu-submenu-top",
      submenuTop + "px"
    );
    // Horizontal position
    this.sidebar.nativeElement.style.setProperty(
      "--sidebar-submenu-submenu-left",
      target.parentElement.offsetLeft + target.offsetLeft + target.offsetWidth + "px"
    );
    this.activeSubmenuSubmenu = submenu.submenu;
    this.activeSubmenuSubmenuSubmenu = null;
  }

  // Submenu of submenu of submenu
  openSubmenuSubmenuSubmenu(target: HTMLElement, submenu: MenuItem): void {
    // Vertical overflow control
    let submenuTop = this.checkTop(target, submenu, true);
    // Vertical position
    this.sidebar.nativeElement.style.setProperty(
      "--sidebar-submenu-submenu-submenu-top",
      submenuTop + "px"
    );
    // Horizontal position
    this.sidebar.nativeElement.style.setProperty(
      "--sidebar-submenu-submenu-submenu-left",
      target.parentElement.offsetLeft + target.offsetLeft + target.offsetWidth + "px"
    );
    this.activeSubmenuSubmenuSubmenu = submenu.submenu;
  }

  // Menu vertical overflow control
  checkTop(target: HTMLElement, menu: MenuItem, submenu?: boolean): number {
    // Window height
    let windowHeight = window.innerHeight;
    // Menu item height
    let menuItemHeight = 48;
    // Total items in menu
    let menuItemsTotal = menu.submenu.filter(submenu => submenu.profile).length;
    // Navbar padding
    let topPadding = 11 * parseFloat(getComputedStyle(document.documentElement).fontSize);
    // Top value
    let topValue =
      target.parentElement.offsetTop - target.parentElement.scrollTop + target.offsetTop - 8;
    // Submenu overflowing the window
    if (submenu && menuItemsTotal * menuItemHeight > windowHeight * 0.9) {
      let windowMaxItems = Math.floor((windowHeight * 0.9) / menuItemHeight);
      topValue = windowHeight - (topPadding - 20 + windowMaxItems * menuItemHeight);
      // Menu overflowing the window
    } else if (topPadding + topValue + menuItemsTotal * menuItemHeight > windowHeight) {
      topValue = windowHeight - (topPadding - 20 + menuItemsTotal * menuItemHeight);
    }
    return topValue;
  }

  // Open sidebar
  openSidebar(): void {
    if (!this.sidebarLocked) {
      this.sidebarOpenTimeout = setTimeout(() => (this.sidebarToggled = false), this.menuOpenDelay);
    }
  }

  // Collapse sidebar
  collapseSidebar(): void {
    clearTimeout(this.sidebarOpenTimeout);
    if (!this.sidebarLocked) {
      this.sidebarCollapseTimeout = setTimeout(() => {
        this.sidebarToggled = true;
      }, this.menuCollapseDelay);
    }
  }

  // Close menu
  closeMenu(): void {
    this.menuClosing = setTimeout(() => {
      if (!this.menuActive) {
        this.activeSubmenuSubmenuSubmenu = null;
        this.activeSubmenuSubmenu = null;
        this.activeSubmenu = null;
      }
    }, this.menuCollapseDelay);
  }

  // Close submenu
  closeSubmenu(): void {
    this.submenuClosing = setTimeout(() => {
      if (!this.submenuActive) {
        this.activeSubmenuSubmenuSubmenu = null;
        this.activeSubmenuSubmenu = null;
      }
    }, this.menuCollapseDelay);
  }

  // Close submenu of submenu
  closeSubmenuSubmenu(): void {
    this.submenuSubmenuClosing = setTimeout(() => {
      if (!this.submenuSubmenuActive) {
        this.activeSubmenuSubmenuSubmenu = null;
      }
    }, this.menuCollapseDelay);
  }

  // Clear menu close timeouts
  clearMenuTimeout(): void {
    if (this.menuClosing) {
      clearTimeout(this.menuClosing);
    }
    if (this.submenuClosing && !this.activeSubmenu?.some(submenu => submenu.submenu)) {
      clearTimeout(this.submenuClosing);
    }
    if (
      this.submenuSubmenuClosing &&
      !this.activeSubmenuSubmenu?.some(submenu => submenu.submenu)
    ) {
      clearTimeout(this.submenuSubmenuClosing);
    }
    if (this.sidebarCollapseTimeout) {
      clearTimeout(this.sidebarCollapseTimeout);
    }
  }

  // Lock sidebar
  lockSidebar(): void {
    this.sidebarLocked = !this.sidebarLocked;
    this.sidebarUpdate.emit(this.sidebarLocked);
  }
}
